import { Typography } from '@/components/atoms/Typography';
import { CircleCheckOutlineIcon, InfoIcon } from '@/components/atoms/icons';
import { useUIColors } from '@/core/hooks';
import React from 'react';
import { StyleSheet, View } from 'react-native';

export type NoteBannerVariant = 'warning' | 'success';

export interface NoteBannerProps {
  variant: NoteBannerVariant;
  title: string;
  description: string;
}

/**
 * Tinted inline banner: icon + title + description.
 * 'warning' = "Important to Note" (Reserve Unit shortfall),
 * 'success' = "Reserved! Awaiting your Signature" (unit status).
 */
export function NoteBanner({ variant, title, description }: Readonly<NoteBannerProps>) {
  const colors = useUIColors();
  const isSuccess = variant === 'success';
  const bg = isSuccess ? colors.successNoteBg : colors.importantNoteBg;
  const border = isSuccess ? colors.successNoteBorder : colors.importantNoteBorder;
  const titleColor = isSuccess ? colors.successNoteTitle : colors.importantNoteTitle;

  return (
    <View style={[styles.container, { backgroundColor: bg, borderColor: border }]}>
      {isSuccess ? (
        <CircleCheckOutlineIcon size={18} color={titleColor} />
      ) : (
        <InfoIcon size={18} color={titleColor} />
      )}
      <View style={styles.textContent}>
        <Typography variant="headline" color={titleColor}>
          {title}
        </Typography>
        <Typography variant="caption" color="secondary">
          {description}
        </Typography>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
  },
  textContent: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
});

export default NoteBanner;
